import { useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Star, ChevronLeft, ChevronRight } from "lucide-react";
import { FadeUp } from "./Reveal";

// SAMPLE reader reviews — swap for real reader quotes as they arrive.
const REVIEWS = [
  {
    quote:
      "The aurora scenes took my breath away — I've never read a superhero story that feels this Canadian. Alex is the reluctant hero you can't help rooting for.",
    name: "S. Tremblay",
    place: "Toronto",
    stars: 5,
  },
  {
    quote:
      "Percy Jackson energy with a distinctly northern soul. The moment the red-dot map lit up, I couldn't put it down.",
    name: "J. Cardinal",
    place: "Vancouver",
    stars: 5,
  },
  {
    quote:
      "I picked this up for my son and ended up reading it myself in one sitting. Fast, warm, and full of heart.",
    name: "M. Okafor",
    place: "Calgary",
    stars: 5,
  },
  {
    quote:
      "Finally a coming-of-age fantasy where friendship and identity matter as much as the powers. Book Two can't come soon enough.",
    name: "R. Whitfield",
    place: "Ottawa",
    stars: 4,
  },
  {
    quote:
      "Sparks, shields, and a city that hums — Northern Lights Rising is pure electricity from page one.",
    name: "A. Delgado",
    place: "Halifax",
    stars: 5,
  },
  {
    quote:
      "A crisp read that feels like a full season of a great show. The Canadian settings make it all feel real.",
    name: "D. Nguyen",
    place: "Edmonton",
    stars: 5,
  },
];

const Stars = ({ n }) => (
  <div className="flex gap-1" aria-label={`${n} out of 5 stars`}>
    {[1, 2, 3, 4, 5].map((i) => (
      <Star
        key={i}
        size={14}
        className={i <= n ? "fill-emerald-400 text-emerald-400" : "text-zinc-700"}
      />
    ))}
  </div>
);

const usePerView = () => {
  const calc = () =>
    typeof window !== "undefined" && window.innerWidth >= 1280
      ? 3
      : typeof window !== "undefined" && window.innerWidth >= 768
        ? 2
        : 1;
  const [perView, setPerView] = useState(calc);
  useEffect(() => {
    const onResize = () => setPerView(calc());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return perView;
};

export const Reviews = () => {
  const perView = usePerView();
  const reduced = useReducedMotion();
  const maxIndex = Math.max(0, REVIEWS.length - perView);
  const [index, setIndex] = useState(0);
  const [hovering, setHovering] = useState(false);

  useEffect(() => {
    setIndex((i) => Math.min(i, maxIndex));
  }, [maxIndex]);

  useEffect(() => {
    if (reduced || hovering || maxIndex === 0) return;
    const t = setInterval(() => setIndex((i) => (i >= maxIndex ? 0 : i + 1)), 6000);
    return () => clearInterval(t);
  }, [reduced, hovering, maxIndex]);

  const prev = () => setIndex((i) => (i <= 0 ? maxIndex : i - 1));
  const next = () => setIndex((i) => (i >= maxIndex ? 0 : i + 1));

  const onDragEnd = (_, info) => {
    if (info.offset.x < -60) next();
    else if (info.offset.x > 60) prev();
  };

  return (
    <section id="reviews" data-testid="reviews-section" className="relative py-28 md:py-36 overflow-hidden">
      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12">
        <FadeUp>
          <div className="flex items-baseline justify-between flex-wrap gap-4">
            <span className="font-tech text-[11px] uppercase tracking-[0.3em] text-emerald-400">
              Reader Reviews
            </span>
            <span className="font-tech text-[11px] uppercase tracking-[0.3em] text-zinc-600">
              Northern Lights Rising
            </span>
          </div>
          <div className="flex flex-wrap items-end justify-between gap-8 mt-8">
            <h2 className="font-display font-light tracking-tighter leading-[1.02] text-[clamp(2.2rem,4.6vw,4.5rem)]">
              What readers <span className="italic text-zinc-400">are saying.</span>
            </h2>
            <div className="flex gap-3">
              <button
                onClick={prev}
                data-testid="review-prev"
                aria-label="Previous reviews"
                className="w-12 h-12 flex items-center justify-center border border-white/15 text-zinc-300 hover:border-emerald-400/60 hover:text-emerald-300 transition-[border-color,color] duration-300"
              >
                <ChevronLeft size={20} />
              </button>
              <button
                onClick={next}
                data-testid="review-next"
                aria-label="Next reviews"
                className="w-12 h-12 flex items-center justify-center border border-white/15 text-zinc-300 hover:border-emerald-400/60 hover:text-emerald-300 transition-[border-color,color] duration-300"
              >
                <ChevronRight size={20} />
              </button>
            </div>
          </div>
        </FadeUp>

        <FadeUp delay={0.1}>
          <div
            className="mt-14 relative border border-white/10 bg-[#0a0a0a] p-5 md:p-8 overflow-hidden"
            data-testid="reviews-carousel"
            onMouseEnter={() => setHovering(true)}
            onMouseLeave={() => setHovering(false)}
          >
            <div className="overflow-hidden">
              <motion.div
                className="flex items-stretch"
                animate={{ x: `-${index * (100 / perView)}%` }}
                transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                drag="x"
                dragConstraints={{ left: 0, right: 0 }}
                dragElastic={0.08}
                onDragEnd={onDragEnd}
                style={{ cursor: "grab" }}
                data-testid="reviews-track"
              >
                {REVIEWS.map((r, i) => (
                  <div
                    key={r.name}
                    className="shrink-0 px-2 first:pl-0 last:pr-0"
                    style={{ width: `${100 / perView}%` }}
                  >
                    <figure
                      data-testid={`review-card-${i + 1}`}
                      className="h-full flex flex-col justify-between gap-8 border border-white/10 bg-[#050505] p-7 hover:border-emerald-400/40 transition-colors duration-300"
                    >
                      <div>
                        <Stars n={r.stars} />
                        <blockquote className="mt-6 font-display font-light italic tracking-tight text-xl md:text-2xl leading-snug text-zinc-200">
                          &ldquo;{r.quote}&rdquo;
                        </blockquote>
                      </div>
                      <figcaption className="font-tech text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                        {r.name} · {r.place}
                      </figcaption>
                    </figure>
                  </div>
                ))}
              </motion.div>
            </div>

            {/* Dots */}
            <div className="flex gap-2.5 mt-8 justify-center" data-testid="review-dots">
              {Array.from({ length: maxIndex + 1 }).map((_, i) => (
                <button
                  key={i}
                  onClick={() => setIndex(i)}
                  aria-label={`Go to reviews page ${i + 1}`}
                  data-testid={`review-dot-${i + 1}`}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    i === index ? "w-8 bg-emerald-400" : "w-1.5 bg-zinc-700 hover:bg-zinc-500"
                  }`}
                />
              ))}
            </div>
          </div>
        </FadeUp>
      </div>
    </section>
  );
};

export default Reviews;
